import type React from "react";
import { cn } from "@/lib/utils";

type BadgeVariant =
  | "default"
  | "outline"
  | "tier-atrisk"
  | "tier-watch"
  | "tier-on-track"
  | "tier-excellent";

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  default: "bg-primary/10 text-primary border-transparent",
  outline: "border border-border bg-transparent text-foreground",
  // Solid tier fills, ordered green(best) -> red(worst) to match every other
  // ranked colour system in the app (health bands, activity tiers) -- this
  // was the one system where "Excellent" rendered orange. Green and orange
  // use the same darkened hue as --pill-text-green/-orange in index.css
  // (kept fixed here, not the token itself: that one brightens in dark mode
  // for text-on-card contrast, which would undo the darkening a solid
  // white-text fill still needs regardless of theme).
  "tier-atrisk": "bg-emergency-red text-white font-semibold border-transparent",
  "tier-watch": "bg-[hsl(33_86%_33%)] text-white font-semibold border-transparent",
  "tier-on-track": "bg-eagle-blue text-white font-semibold border-transparent",
  "tier-excellent": "bg-[hsl(117_41%_30%)] text-white font-semibold border-transparent",
};

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

export function Badge({
  variant = "default",
  className,
  ...props
}: BadgeProps): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold font-head border whitespace-nowrap",
        VARIANT_CLASSES[variant],
        className,
      )}
      {...props}
    />
  );
}
